/** @typedef {'light' | 'dark'} ArchonThemeId */

export const ARCHON_THEMES = Object.freeze({
  LIGHT: 'light',
  DARK: 'dark',
});

/** @type {ArchonThemeId} */
export const DEFAULT_THEME = ARCHON_THEMES.LIGHT;

export const THEME_STORAGE_KEY = 'archon.theme';

/** @param {unknown} value */
export function isValidTheme(value) {
  return value === ARCHON_THEMES.LIGHT || value === ARCHON_THEMES.DARK;
}

/** @param {unknown} value @returns {ArchonThemeId} */
export function normalizeTheme(value) {
  return isValidTheme(value) ? value : DEFAULT_THEME;
}

/* eslint-disable no-restricted-syntax -- JS-side palette source for canvas-rendered
   charts (Chart.js) and WebGL/shader backgrounds below; these can't read CSS custom
   properties, so the literal hex values here are the actual source of truth, not a
   copy of the CSS tokens. */

/** Chart palette keyed by theme (Recharts / canvas). */
export const chartPalette = Object.freeze({
  light: Object.freeze({
    primary: '#1a5a96',
    secondary: '#2679bb',
    accent: '#f8a233',
    grid: 'rgba(13, 44, 75, 0.12)',
    text: '#212529',
    muted: '#6c757d',
  }),
  dark: Object.freeze({
    primary: '#2679bb',
    secondary: '#6ea8ff',
    accent: '#f8a233',
    grid: 'rgba(255, 255, 255, 0.12)',
    text: '#f0f6ff',
    muted: 'rgba(255, 255, 255, 0.72)',
  }),
});

/** @param {ArchonThemeId} theme */
export function getChartPalette(theme) {
  return chartPalette[theme] ?? chartPalette.light;
}

/** LiquidEther fluid waves — palette [base, wave mid, wave accent]. */
export const liquidEtherTheme = Object.freeze({
  dark: Object.freeze({
    colors: ['#0c192c', '#2679bb', '#f8a233'],
    fallbackBg: '#0c192c',
    mouseForce: 20,
    cursorSize: 100,
    resolution: 0.5,
    autoDemo: true,
    autoSpeed: 0.5,
    autoIntensity: 2.2,
  }),
  light: Object.freeze({
    colors: ['#f0f4f8', '#b8d4eb', '#2679bb'],
    fallbackBg: '#f0f4f8',
    mouseForce: 18,
    cursorSize: 100,
    resolution: 0.5,
    autoDemo: true,
    autoSpeed: 0.45,
    autoIntensity: 1.85,
  }),
});

/** @param {ArchonThemeId} theme */
export function getLiquidEtherTheme(theme) {
  return liquidEtherTheme[theme] ?? liquidEtherTheme.light;
}

/** Silk wave shader — one base color per theme (fragment shader multiplies wave bands). */
export const silkTheme = Object.freeze({
  dark: Object.freeze({
    color: '#0c192c',
    speed: 5,
    scale: 1,
    noiseIntensity: 1.5,
    rotation: 0,
    fallbackBg: '#0c192c',
  }),
  light: Object.freeze({
    color: '#b8d4eb',
    speed: 5,
    scale: 1,
    noiseIntensity: 1.15,
    rotation: 0,
    fallbackBg: '#f0f4f8',
  }),
});

/** @param {ArchonThemeId} theme */
export function getSilkTheme(theme) {
  return silkTheme[theme] ?? silkTheme.light;
}
/* eslint-enable no-restricted-syntax */
